import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as QUERY_KEY_GET_ALL } from './useGetPayments';
import { QUERY_KEY as QUERY_KEY_GET_STRIPE_SUBSCRIPTIONS } from './useGetStripeSubscriptions';

export interface UpdateSubscriptionDateRequest {
  subscriptionId: string;
  nextPaymentDate: string; // ISO date string
  tenant_id?: string; // Optional tenant_id for property managers
}

export interface UpdateSubscriptionDateResponse {
  success: boolean;
  message: string;
  subscriptionId: string;
}

const METHOD = 'POST';
const ENDPOINT = 'payments/subscription/update-date';

export const useUpdateSubscriptionDate = (
  stateFns?: MutationStateFns<UpdateSubscriptionDateResponse>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateSubscriptionDateRequest) => {
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      // Call the backend API to update the subscription date
      const response = await handleApiCall<UpdateSubscriptionDateResponse, typeof data>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {}),
        data
      );

      return response;
    },
    onSuccess: data => {
      enqueueSnackbar(data.message || 'Payment date updated successfully', { variant: 'success' });

      // Invalidate both payment and subscription queries to refresh the lists
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GET_ALL(),
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GET_STRIPE_SUBSCRIPTIONS(),
      });

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar(
        (err as AxiosError<{ message: string }>)?.response?.data?.message ||
          'Failed to update payment date. Please try again.',
        { variant: 'error' }
      );

      stateFns?.onError?.(err);
    },
  });
};
